"use client";

import { memo, useCallback, useState } from "react";
import { useAuth } from "@clerk/nextjs";
import { DynamicFormRenderer } from "@/components/form/DynamicFormRenderer";

type FormFieldDef = {
  id: string;
  type: string;
  label: string;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  options?: string[];
  validation?: Record<string, unknown>;
  sortOrder: number;
};

interface BuyerFormModalProps {
  eventId: string;
  fields: FormFieldDef[];
  onComplete: () => void;
  onSkip: () => void;
}

export const BuyerFormModal = memo(function BuyerFormModal({
  eventId,
  fields,
  onComplete,
  onSkip,
}: BuyerFormModalProps) {
  const { getToken } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (responses: Record<string, unknown>) => {
      setError(null);
      try {
        const token = await getToken();
        const apiBase = process.env.NEXT_PUBLIC_API_URL || "";
        const res = await fetch(
          `${apiBase}/events/${eventId}/form-response`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ responses }),
          }
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || "Failed to submit form");
        }
        onComplete();
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Something went wrong");
      }
    },
    [eventId, getToken, onComplete]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="buyer-form-modal-title" className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0b0c0f] p-6 shadow-2xl">
        <div className="mb-5">
          <h2 id="buyer-form-modal-title" className="text-lg font-semibold text-white">
            Almost there!
          </h2>
          <p className="text-sm text-white/50 mt-1">
            Please fill in the following information to receive your ticket.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2 text-sm text-red-400">
            {error}
          </div>
        )}

        <DynamicFormRenderer
          fields={fields}
          onSubmit={handleSubmit}
          submitLabel="Submit & Get Ticket"
          onSkip={onSkip}
          skipLabel="Skip for now"
        />
      </div>
    </div>
  );
});
